import { Fragment } from 'react'
import {
  Box,
  BoxProps,
  Container,
  Divider,
  Grid,
  Link,
  Stack,
  SvgIcon,
  Typography
} from '@mui/material'
import parse from 'html-react-parser'
import Image from 'next/image'

import LanguageSwitcher from '@/components/base/language-switcher'
import { useAppStore } from '@/contexts/store'
import { isHappa } from '@/hooks/use-brand'
import { nl2br } from '@/utils'

const Footer = (props: BoxProps) => {
  const { menu, strings } = useAppStore()

  const footerMenus = ['footer_menu_01', 'footer_menu_02', 'footer_menu_03', 'footer_menu_04']

  return (
    <Box component="footer" {...props}>
      <Container>
        <Grid container gap={4}>
          <Grid size={{ xs: 4, sm: 8, md: 12 }}>
            <Divider />
          </Grid>

          <Grid
            container
            gap={{ xs: 2, sm: 3, md: 4 }}
            size={{ xs: 4, sm: 8, md: 12 }}
            columns={{ xs: 2, sm: 3, lg: 5 }}
            alignItems="start"
          >
            <Grid container direction="column" size={1} gap={2} flexGrow={{ lg: 1 }}>
              <Typography
                variant="textRegular"
                color="primary"
                fontWeight={isHappa ? 700 : 500}
                fontFamily={isHappa ? 'var(--heading-font)' : 'var(--base-font)'}
                sx={{ m: 0 }}
                flexGrow={1}
              >
                {strings?.footer?.col_title}
              </Typography>

              {strings?.footer?.address ? (
                <Typography
                  variant="textSmall"
                  color="text.primary"
                  component="span"
                  flexGrow={1}
                  sx={{
                    '& a': {
                      color: (theme) => theme.palette.text.primary
                    },
                    '& p': {
                      m: (theme) => `0 0 ${theme.spacing(1)}`
                    },
                    '& p:last-of-type': {
                      m: 0
                    }
                  }}
                >
                  {parse(nl2br(strings?.footer?.address || ''))}
                </Typography>
              ) : null}

              {strings?.footer?.phone ? (
                <Link
                  href={`tel:${strings?.footer.phone?.replace(/[^\d+]/g, '')}`}
                  sx={{ textDecoration: 'none', display: 'flex' }}
                >
                  <Typography
                    variant="textSmall"
                    color="text.primary"
                    component="span"
                    flexGrow={1}
                  >
                    {strings?.footer.phone}
                  </Typography>
                </Link>
              ) : null}

              {strings?.footer?.mail ? (
                <Link
                  href={`mailto:${strings?.footer.mail}`}
                  sx={{ textDecoration: 'none', display: 'flex' }}
                >
                  <Typography
                    variant="textSmall"
                    color="text.primary"
                    component="span"
                    flexGrow={1}
                  >
                    {strings?.footer.mail}
                  </Typography>
                </Link>
              ) : null}
            </Grid>

            {footerMenus.map((menuKey) => {
              const currentMenu = menu?.[menuKey as keyof typeof menu]
              return currentMenu?.menu && currentMenu.menu.length > 0 ? (
                <Grid
                  key={menuKey}
                  container
                  direction="column"
                  size={1}
                  gap={2}
                  flexGrow={{ lg: 1 }}
                >
                  <Typography
                    variant="textRegular"
                    color="primary"
                    fontWeight={isHappa ? 700 : 500}
                    flexGrow={1}
                    fontFamily={isHappa ? 'var(--heading-font)' : 'var(--base-font)'}
                  >
                    {currentMenu?.acf?.title}
                  </Typography>

                  <Box component="nav" aria-label={currentMenu?.acf?.title} flexGrow={1}>
                    <Box
                      component="ul"
                      sx={{
                        listStyle: 'none',
                        p: 0,
                        m: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2
                      }}
                    >
                      {currentMenu?.menu.map((item, index) => (
                        <li key={index.toString()}>
                          <Link
                            href={item.url}
                            sx={{
                              display: 'flex',
                              color: (theme) => theme.palette.text.primary,
                              textDecoration: 'none',
                              '&:hover': {
                                textDecoration: 'underline'
                              }
                            }}
                          >
                            <Typography variant="textSmall" color="text.primary">
                              {item.title}
                            </Typography>
                          </Link>
                        </li>
                      ))}
                    </Box>
                  </Box>
                </Grid>
              ) : null
            })}
          </Grid>

          <Stack
            component={Grid}
            size={{ xs: 4, sm: 8, md: 12 }}
            direction={{ sm: 'row' }}
            alignItems={{ sm: 'center' }}
            justifyContent="space-between"
            sx={{ mb: { xs: -2, sm: 0 }, mt: { xs: 2, lg: 0 } }}
            gap={4}
          >
            <Image
              src="/images/logo-ceur.png"
              alt="Fondazione CEUR"
              width={113}
              height={64}
              style={{ mixBlendMode: 'multiply' }}
            />

            <Stack
              direction="row"
              alignItems="center"
              justifyContent={{ xs: 'space-between' }}
              gap={2}
            >
              <Stack
                component="ul"
                direction="row"
                alignItems="center"
                sx={{ listStyle: 'none', gap: 2.5, m: 0, p: 0 }}
              >
                {menu?.social_menu?.menu?.map((item, index) => (
                  <li key={index.toString()}>
                    <Link
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={item.title}
                      sx={{
                        display: 'flex',
                        p: 1,
                        m: -1,
                        color: (theme) => theme.palette.primary.main
                      }}
                    >
                      <SvgIcon>
                        <use href={`#ico-${item.title.toLowerCase()}`} />
                      </SvgIcon>
                    </Link>
                  </li>
                ))}
              </Stack>

              <LanguageSwitcher />
            </Stack>
          </Stack>

          <Divider
            component={Grid}
            size={{ xs: 4, sm: 8, md: 12 }}
            sx={{ mb: { xs: -2, sm: 0 } }}
          />

          <Stack
            component={Grid}
            direction="row"
            alignItems="baseline"
            justifyContent="center"
            gap="0.4rem"
            size={{ xs: 4, sm: 8, md: 12 }}
            sx={{ pb: { xs: 2, sm: 4 } }}
          >
            {strings?.footer?.copyright ? (
              <Typography
                component="span"
                variant="textSmall"
                color="text.secondary"
                sx={{
                  '& a': {
                    color: (theme) => theme.palette.text.secondary
                  }
                }}
              >
                {parse(strings?.footer?.copyright || '')}
              </Typography>
            ) : null}

            {menu?.legals_menu?.menu?.map(({ title, url, target }, index) => (
              <Fragment key={`legal-${index}`}>
                <Typography component="span" variant="textSmall" color="text.secondary">
                  ·
                </Typography>
                <Link href={url} target={target} color="text.secondary">
                  <Typography component="span" variant="textSmall">
                    {title}
                  </Typography>
                </Link>
              </Fragment>
            ))}
          </Stack>
        </Grid>
      </Container>
    </Box>
  )
}

export default Footer
